/**
 * Étape 5 — Coordonnées du foyer.
 *
 * Champs collectés :
 *  - Nom · Prénom
 *  - Téléphone (avec indicatif international via PhoneInput)
 *  - E-mail (techniquement nécessaire pour transmettre l'estimation)
 *  - Adresse + code postal (pour le suivi du dossier MPR)
 *
 * Soumet le lead à l'API à la transition vers la page Résultat.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, AtSign, CheckCircle2, Loader2, Lock, MapPin, User } from 'lucide-react';
import { z } from 'zod';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { PhoneInput, type PhoneInputValue } from '../ComparateurFunnel/PhoneInput';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import { apiFetch, ApiError } from '../../../lib/api';
import { captureEvent } from '../../../lib/analytics';
import {
  trackSubmitStart,
  trackSubmitSuccess,
  trackSubmitFailure,
  readAttribution,
} from '../../../lib/tracking';
import { TrustBar } from '../../conversion/TrustBar';
import { aidsStepCounterLabel, useAidsStore } from './useAidsStore';
import { computeAids } from './compute';
import { buildEstimateLine } from '@shared/lib/estimate-amount';

/* ─── Validation locale ──────────────────────────────────── */

const contactSchema = z.object({
  firstName: z.string().min(2, 'Au moins 2 caractères').max(60, 'Trop long').trim(),
  lastName: z.string().min(2, 'Au moins 2 caractères').max(60, 'Trop long').trim(),
  email: z.string().email('Adresse e-mail invalide').trim(),
  phoneNational: z
    .string()
    .max(20, 'Numéro trop long')
    .refine((v) => v === '' || v.replace(/\D/g, '').length >= 6, 'Numéro trop court')
    .optional(),
  address: z.string().max(120).optional(),
  postalCode: z.string().regex(/^\d{5}$/, 'Code postal sur 5 chiffres'),
});

type ContactKey = keyof z.infer<typeof contactSchema>;

export function Step5Contact() {
  const profile = useAidsStore((s) => s.profile);
  const location = useAidsStore((s) => s.location);
  const contact = useAidsStore((s) => s.contact);
  const submitting = useAidsStore((s) => s.submitting);
  const submitError = useAidsStore((s) => s.submitError);
  const patchContact = useAidsStore((s) => s.patchContact);
  const setSubmitting = useAidsStore((s) => s.setSubmitting);
  const setSubmitError = useAidsStore((s) => s.setSubmitError);
  const setLeadId = useAidsStore((s) => s.setLeadId);
  const goNext = useAidsStore((s) => s.goNext);

  const [issues, setIssues] = useState<Partial<Record<ContactKey, string>>>({});
  const [consentMarketing, setConsentMarketing] = useState(false);

  const phone: PhoneInputValue = {
    countryIso2: contact.phoneCountry,
    countryDial: contact.phoneDial,
    national: contact.phoneNational,
  };

  // Pré-remplit le code postal depuis la commune choisie à l'étape 1
  useEffect(() => {
    if (location && !contact.postalCode) {
      patchContact({ postalCode: location.codePostal });
    }
  }, [location, contact.postalCode, patchContact]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitError(null);

    const candidate = {
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phoneNational: contact.phoneNational?.replace(/\D/g, '') || undefined,
      address: contact.address,
      postalCode: contact.postalCode,
    };

    const result = contactSchema.safeParse(candidate);
    if (!result.success) {
      const errs: Partial<Record<ContactKey, string>> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as ContactKey | undefined;
        if (key) errs[key] = issue.message;
      }
      setIssues(errs);
      return;
    }
    setIssues({});

    // Segment CRM selon le profil (occupant/locataire = particulier,
    // bailleur = segment dédié, copropriété = syndic).
    const segment =
      profile === 'copropriete' ? 'syndic' : profile === 'bailleur' ? 'bailleur' : 'particulier';

    const aids_ctx = {
      funnelId: 'aids_calculator' as const,
      intent: 'renover',
      source: 'calculator',
    };
    trackSubmitStart(aids_ctx, { profile, travaux_count: useAidsStore.getState().travaux.length });
    setSubmitting(true);
    try {
      const phoneFull = result.data.phoneNational
        ? `+${contact.phoneDial}${result.data.phoneNational}`
        : undefined;
      const state = useAidsStore.getState();
      const estimation = computeAids(state, state.location?.anahZone ?? 'B2C');
      const message = [
        estimation.totalMax > 0
          ? buildEstimateLine(estimation.totalMin, estimation.totalMax)
          : null,
        `Profil: ${profile ?? '-'}`,
        `Logement: ${state.logement ?? '-'}`,
        location ? `Commune: ${location.nom} (${location.codePostal})` : null,
        result.data.address
          ? `Adresse: ${result.data.address}, ${result.data.postalCode}`
          : `Code postal: ${result.data.postalCode}`,
        `Travaux: ${state.travaux.join(', ') || '-'}`,
        state.rfr != null ? `RFR: ${state.rfr} € · foyer ${state.householdSize}` : null,
        estimation.totalMax > 0
          ? `Détail: MPR ${estimation.mprMin}–${estimation.mprMax} € · CEE ${estimation.ceeMin}–${estimation.ceeMax} €`
          : null,
      ]
        .filter(Boolean)
        .join('\n');

      const res = await apiFetch<{ id: string }>('/api/leads', {
        json: {
          email: result.data.email,
          ...(phoneFull ? { phone: phoneFull } : {}),
          firstName: result.data.firstName,
          lastName: result.data.lastName,
          message,
          source: 'calculator',
          segment,
          intent: 'renover',
          consentMarketing,
          ...readAttribution(),
        },
      });

      captureEvent('aids_calculator_submit', {
        tool: 'calculer-mes-aides',
        profile,
        travaux_count: state.travaux.length,
        estimate_min: estimation.totalMin,
        estimate_max: estimation.totalMax,
      });
      trackSubmitSuccess(aids_ctx, res.id, { email: result.data.email, profile });

      setLeadId(res.id);
      goNext();
    } catch (err) {
      const errMsg =
        err instanceof ApiError
          ? err.message
          : "Une erreur est survenue lors de l'envoi. Réessayez.";
      trackSubmitFailure(aids_ctx, err instanceof ApiError ? 'api' : 'unknown');
      setSubmitError(errMsg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
        {aidsStepCounterLabel(4, profile)}
      </p>
      <h2 className="h-section mt-2">Vos coordonnées</h2>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-500">
        Pour vous transmettre votre estimation détaillée et préparer votre dossier d'aides. Un
        conseiller vous recontacte sous 24 h ouvrées (sans engagement).
      </p>

      {submitError && (
        <Alert variant="error" className="mt-4">
          <AlertDescription className="text-[12.5px]">{submitError}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        {/* ── Prénom + Nom ─────────────────────────────────── */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            id="contact-firstname"
            label="Prénom"
            error={issues.firstName}
            icon={<User className="h-4 w-4" />}
          >
            <Input
              id="contact-firstname"
              autoComplete="given-name"
              required
              value={contact.firstName}
              onChange={(e) => patchContact({ firstName: e.target.value })}
              placeholder="Jean"
              className="h-12 rounded-xl border-2 border-stone-200 pl-10 text-[15px] focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
              aria-invalid={!!issues.firstName}
            />
          </Field>

          <Field id="contact-lastname" label="Nom" error={issues.lastName}>
            <Input
              id="contact-lastname"
              autoComplete="family-name"
              required
              value={contact.lastName}
              onChange={(e) => patchContact({ lastName: e.target.value })}
              placeholder="Dupont"
              className="h-12 rounded-xl border-2 border-stone-200 text-[15px] focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
              aria-invalid={!!issues.lastName}
            />
          </Field>
        </div>

        {/* ── E-mail ───────────────────────────────────────── */}
        <Field
          id="contact-email"
          label="E-mail"
          error={issues.email}
          icon={<AtSign className="h-4 w-4" />}
        >
          <Input
            id="contact-email"
            type="email"
            autoComplete="email"
            required
            value={contact.email}
            onChange={(e) => patchContact({ email: e.target.value })}
            placeholder="vous@email.com"
            className="h-12 rounded-xl border-2 border-stone-200 pl-10 text-[15px] focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
            aria-invalid={!!issues.email}
          />
        </Field>

        {/* ── Téléphone ────────────────────────────────────── */}
        <div>
          <Label htmlFor="contact-phone" className="text-[13px] font-semibold text-ink">
            Téléphone <span className="font-normal text-stone-400">(avec indicatif)</span>
          </Label>
          <div className="mt-1.5">
            <PhoneInput
              id="contact-phone"
              value={phone}
              onChange={(v) =>
                patchContact({
                  phoneCountry: v.countryIso2,
                  phoneDial: v.countryDial,
                  phoneNational: v.national,
                })
              }
              error={issues.phoneNational}
            />
          </div>
          <p className="mt-1.5 text-[11px] text-stone-400">
            Optionnel — accélère le traitement de votre dossier d'aides.
          </p>
        </div>

        {/* ── Adresse + CP ─────────────────────────────────── */}
        <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
          <Field
            id="contact-address"
            label="Adresse du logement"
            hint="Optionnel — accélère la constitution de votre dossier"
            error={issues.address}
            icon={<MapPin className="h-4 w-4" />}
          >
            <Input
              id="contact-address"
              autoComplete="street-address"
              value={contact.address}
              onChange={(e) => patchContact({ address: e.target.value })}
              placeholder="12 rue de la République"
              className="h-12 rounded-xl border-2 border-stone-200 pl-10 text-[15px] focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
              aria-invalid={!!issues.address}
            />
          </Field>

          <Field id="contact-cp" label="Code postal" error={issues.postalCode}>
            <Input
              id="contact-cp"
              inputMode="numeric"
              autoComplete="postal-code"
              required
              maxLength={5}
              value={contact.postalCode}
              onChange={(e) =>
                patchContact({
                  postalCode: e.target.value.replace(/[^\d]/g, '').slice(0, 5),
                })
              }
              placeholder="69000"
              className="h-12 rounded-xl border-2 border-stone-200 text-center text-[15px] tabular-nums focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
              aria-invalid={!!issues.postalCode}
            />
          </Field>
        </div>

        {/* ── Consentement marketing ───────────────────────── */}
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-stone-200 bg-stone-50/50 p-3 transition-colors hover:bg-stone-50">
          <span
            className={cn(
              'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors',
              consentMarketing ? 'border-brand-500 bg-brand-500' : 'border-stone-300 bg-white',
            )}
          >
            {consentMarketing && (
              <motion.span
                initial={{ opacity: 0, scale: 0.4 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.15, ease: easeStripe }}
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-white" strokeWidth={2.4} />
              </motion.span>
            )}
            <input
              type="checkbox"
              checked={consentMarketing}
              onChange={(e) => setConsentMarketing(e.target.checked)}
              className="sr-only"
              aria-label="Recevoir des communications de Solution Éco France"
            />
          </span>
          <span className="text-[12px] leading-relaxed text-stone-600">
            J'accepte de recevoir l'actualité des aides à la rénovation et les offres de Solution
            Éco France. Désabonnement en 1 clic. <span className="text-stone-400">(optionnel)</span>
          </span>
        </label>

        {/* ── RGPD ────────────────────────────────────────── */}
        <div className="flex items-start gap-2 rounded-lg border border-stone-200 bg-stone-50/60 px-3 py-2.5">
          <Lock
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400"
            strokeWidth={1.8}
            aria-hidden
          />
          <p className="text-[11.5px] leading-relaxed text-stone-500">
            Données traitées par Solution Éco France pour vous transmettre votre estimation et
            assurer le suivi de votre dossier d'aides à la rénovation (RGPD art. 6.1.b).{' '}
            <a
              href="/legal/politique-confidentialite"
              className="font-medium text-brand-700 hover:underline"
            >
              Politique de confidentialité
            </a>
          </p>
        </div>

        <Button
          type="submit"
          variant="brand"
          size="xl"
          disabled={submitting}
          className="w-full gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Envoi en cours…
            </>
          ) : (
            <>
              Recevoir mon estimation détaillée
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>

        <TrustBar variant="inline" max={4} className="pt-1" />
      </form>
    </div>
  );
}

/* ─── Field wrapper ─────────────────────────────────────── */

function Field({
  id,
  label,
  hint,
  error,
  icon,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={id} className="text-[13px] font-semibold text-ink">
        {label}
      </Label>
      <div className="relative mt-1.5">
        {icon && (
          <span
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
          >
            {icon}
          </span>
        )}
        {children}
      </div>
      {error ? (
        <p className="mt-1 text-[12px] font-medium text-red-600" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-[11px] text-stone-400">{hint}</p>
      ) : null}
    </div>
  );
}
